import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { AppShell } from "@/components/AppShell";

import { LoginPage } from "@/pages/LoginPage";
import { ChangePasswordPage } from "@/pages/ChangePasswordPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { TransactionsPage } from "@/pages/TransactionsPage";
import { RetraitPage } from "@/pages/RetraitPage";
import { CashiersBalancesPage } from "@/pages/CashiersBalancesPage";
import { PosLocationsPage } from "@/pages/PosLocationsPage";
import { ConfigurationPage } from "@/pages/ConfigurationPage";
import { OperationsPage } from "@/pages/OperationsPage";
import { SupportPage } from "@/pages/SupportPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { UpdateBanner } from "@/components/UpdateBanner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={["merchant"] as const}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

/** Redirige `/` selon l'auth : Dashboard si connecté, sinon Login. */
function HomeRedirect() {
  const auth = useAuth();
  if (auth.status === "loading") return null;
  return auth.status === "authenticated"
    ? <Navigate to="/dashboard" replace />
    : <Navigate to="/login" replace />;
}

function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="text-5xl font-bold text-brand-700 mb-2">404</div>
        <div className="text-slate-600">Page non trouvée</div>
      </div>
    </div>
  );
}

function ForbiddenPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="text-center max-w-md">
        <div className="text-5xl font-bold text-red-700 mb-2">403</div>
        <div className="text-slate-700 font-medium mb-1">Accès refusé</div>
        <div className="text-sm text-slate-500">
          Votre rôle n'a pas la permission d'accéder à cette page. Le nouveau dashboard n'est
          ouvert qu'aux comptes <strong>merchant</strong> pour l'instant.
        </div>
      </div>
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <UpdateBanner />
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forbidden" element={<ForbiddenPage />} />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={<Shell><DashboardPage /></Shell>}
            />
            <Route
              path="/historique-de-transactions"
              element={<Shell><TransactionsPage /></Shell>}
            />
            <Route
              path="/retrait"
              element={<Shell><RetraitPage /></Shell>}
            />
            <Route
              path="/cashiers/balances"
              element={<Shell><CashiersBalancesPage /></Shell>}
            />
            <Route
              path="/pos/locations"
              element={<Shell><PosLocationsPage /></Shell>}
            />
            <Route
              path="/configuration"
              element={<Shell><ConfigurationPage /></Shell>}
            />
            <Route
              path="/operations"
              element={<Shell><OperationsPage /></Shell>}
            />
            <Route
              path="/support"
              element={<Shell><SupportPage /></Shell>}
            />
            <Route
              path="/profile"
              element={<Shell><ProfilePage /></Shell>}
            />
            {/* Aliases legacy */}
            <Route path="/tickets" element={<Navigate to="/support" replace />} />
            <Route path="/agents" element={<Navigate to="/operations" replace />} />
            <Route path="/stores" element={<Navigate to="/operations" replace />} />
            <Route path="/pos" element={<Navigate to="/operations" replace />} />
            <Route path="/overview" element={<Navigate to="/dashboard" replace />} />
            <Route path="/transactions" element={<Navigate to="/historique-de-transactions" replace />} />
            <Route path="/transfert" element={<Navigate to="/retrait" replace />} />
            {/* /historique-de-retraits est intentionnellement redirigé vers l'historique global (filtre Type=Retrait) */}
            <Route path="/historique-de-retraits" element={<Navigate to="/historique-de-transactions" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
